import dayjs from "dayjs";
import {
    ALL_CHANNELS,
    BRANDED_NON_BRANDED,
    EGraphTabs,
    ORGANIC_PAID,
    timeGranularityList,
} from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Helpers/SearchOverviewGraphConfig";
import type {
    IInsightsMetaData,
    IInsightsProps } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/SearchTrafficInsights/typesAndConstants";
import {
    channels,
    EInsightsTypes,
    EInsightTypes,
    EPriorities,
    formatters,
    GREEN,
    legends,
    RED,
} from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/SearchTrafficInsights/typesAndConstants";
import {
    commonInsightsValueCalculator,
    commonIsJumpAboveThreshold,
    getConsolidateDataStructure,
    isAboveTrafficThreshold,
} from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/SearchTrafficInsights/utilities";

export const insightsMetaData: IInsightsMetaData = {
    [EInsightsTypes.ORGANIC_CHANNEL]: {
        priority: EPriorities.VERY_IMPORTANT,
        apiEntry: "TrafficShare",
        monthlyJumpThreshold: 0.1,
        weeklyTrendingThreshold: 0.1,
        monthlyTrendingThreshold: 0.05,
        channelType: channels.ORGANIC,
        displayName: "search.overview.insights.organic.display.name",
        selectedTabOnClick: EGraphTabs.TrafficShare,
        unSelectedLegend: legends.PAID,
        selectedLegend: legends.ORGANIC,
        selectedCategoryOnClick: ORGANIC_PAID,
        formatter: formatters.PERCENTAGE,
        isJumpAboveThreshold: commonIsJumpAboveThreshold(0.2, 2),
        supportNewChannel: true,
        getInsight: (rawData, id, filters) => {
            const channelData = rawData.enrichedData[filters.granularity].ORGANIC_PAIDData?.Data;
            return commonInsightsValueCalculator(channelData, id, insightsMetaData[id], filters);
        },
    },
    [EInsightsTypes.PAID_CHANNEL]: {
        priority: EPriorities.SOMEWHAT_IMPORTANT,
        monthlyJumpThreshold: 0.1,
        weeklyTrendingThreshold: 0.1,
        monthlyTrendingThreshold: 0.05,
        apiEntry: "TrafficShare",
        channelType: channels.PAID,
        displayName: "search.overview.insights.paid.display.name",
        selectedTabOnClick: EGraphTabs.TrafficShare,
        unSelectedLegend: legends.ORGANIC,
        selectedLegend: legends.PAID,
        selectedCategoryOnClick: ORGANIC_PAID,
        formatter: formatters.PERCENTAGE,
        isJumpAboveThreshold: commonIsJumpAboveThreshold(0.2, 2),
        supportNewChannel: true,
        getInsight: (rawData, id, filters) => {
            const channelData = rawData.enrichedData[filters.granularity].ORGANIC_PAIDData?.Data;
            return commonInsightsValueCalculator(channelData, id, insightsMetaData[id], filters);
        },
    },
    [EInsightsTypes.BRANDED_TRAFFIC]: {
        priority: EPriorities.EXTREMELY_IMPORTANT,
        monthlyJumpThreshold: 0.1,
        weeklyTrendingThreshold: 0.1,
        monthlyTrendingThreshold: 0.05,
        apiEntry: "TrafficShare",
        channelType: channels.BRANDED,
        displayName: "search.overview.insights.branded.display.name",
        selectedTabOnClick: EGraphTabs.TrafficShare,
        selectedCategoryOnClick: BRANDED_NON_BRANDED,
        formatter: formatters.PERCENTAGE,
        dataParser: (channelType) => (dataBreakDown) =>
            Object.entries(dataBreakDown).map((a) => ({
                x: dayjs.utc(a[0]),
                y: a[1][channelType] / a[1][ALL_CHANNELS],
            })),
        getInsight: (rawData, id, filters) => {
            const channelData = rawData.enrichedData.Monthly.BRANDED_NON_BRANDEDData?.Data;
            const isWeekly = filters.granularity === timeGranularityList.weekly.name;
            const trafficData = rawData.enrichedData.Monthly.ORGANIC_PAIDData?.Data;
            const { apiEntry } = insightsMetaData[id];
            if (isWeekly || !trafficData?.[apiEntry]) {return {};}

            const { granularity, lastSupportedDate, trafficThreshold } = filters;
            const aboveTrafficThreshold = isAboveTrafficThreshold(
                rawData.enrichedData.Monthly.ORGANIC_PAIDData?.Data,
                trafficThreshold,
                channels.ALL,
                lastSupportedDate,
                granularity,
                getConsolidateDataStructure,
            );
            return commonInsightsValueCalculator(channelData, id, insightsMetaData[id], filters, aboveTrafficThreshold);
        },
    },
};

export const defaultInsightsProps: IInsightsProps = {
    [EInsightTypes.NEW_CHANNEL]: {
        headerColor: GREEN,
        headerKey: "search.overview.insights.new.channel.header",
        bodyKey: (duration: string) =>
            duration === timeGranularityList.weekly.name
                ? "search.overview.insights.new.channel.body.weekly"
                : "search.overview.insights.new.channel.body",
        headerIcon: "new",
    },
    [EInsightTypes.TRENDING_UP]: {
        headerColor: GREEN,
        headerKey: "search.overview.insights.trend.up.header",
        bodyKey: (duration: string) =>
            duration === timeGranularityList.weekly.name
                ? "search.overview.insights.trend.up.body.weekly"
                : "search.overview.insights.trend.up.body",
        headerIcon: "trend-up",
    },
    [EInsightTypes.TRENDING_DOWN]: {
        headerColor: RED,
        headerKey: "search.overview.insights.trend.down.header",
        bodyKey: (duration: string) =>
            duration === timeGranularityList.weekly.name
                ? "search.overview.insights.trend.down.body.weekly"
                : "search.overview.insights.trend.down.body",
        headerIcon: "trend-down",
    },
    [EInsightTypes.INCREASING]: {
        headerColor: GREEN,
        headerKey: "search.overview.insights.increasing.header",
        bodyKey: (duration: string) =>
            duration === timeGranularityList.weekly.name
                ? "search.overview.insights.increasing.body.weekly"
                : "search.overview.insights.increasing.body",
        headerIcon: "arrow-up",
    },
    [EInsightTypes.DECREASING]: {
        headerColor: RED,
        headerKey: "search.overview.insights.decreasing.header",
        bodyKey: (duration: string) =>
            duration === timeGranularityList.weekly.name
                ? "search.overview.insights.decreasing.body.weekly"
                : "search.overview.insights.decreasing.body",
        headerIcon: "arrow-down",
    },
};

const pagePerVisitsAndVisitDurationDefaultInsightsProps = Object.fromEntries(
    Object.entries(defaultInsightsProps).map((defaultInsightProps) => {
        const insightType = Number(defaultInsightProps[0]);
        const decreasingBodyKey = (duration: string) =>
            duration === timeGranularityList.weekly.name
                ? "search.overview.insights.decreasing.body.weekly.abs"
                : "search.overview.insights.decreasing.body.abs";
        const increasingBodyKey = (duration: string) =>
            duration === timeGranularityList.weekly.name
                ? "search.overview.insights.increasing.body.weekly.abs"
                : "search.overview.insights.increasing.body.abs";
        const defaultBodyKey = defaultInsightProps[1].bodyKey;
        const bodyKey =
            insightType === EInsightTypes.DECREASING
                ? decreasingBodyKey
                : insightType === EInsightTypes.INCREASING
                ? increasingBodyKey
                : defaultBodyKey;
        return [defaultInsightProps[0], { ...defaultInsightProps[1], bodyKey }];
    }),
);

const bounceRateDefaultInsightsProps = Object.fromEntries(
    Object.entries(defaultInsightsProps).map((defaultInsightProps) => {
        // bounce rate colors are opposite
        const headerColor = defaultInsightProps[1].headerColor === GREEN ? RED : GREEN;
        return [defaultInsightProps[0], { ...defaultInsightProps[1], headerColor }];
    }),
);
